import { Activity, AlertTriangle, RefreshCw, RotateCcw, Wifi } from "lucide-react";
import type { Channel, PlaybackSession, SessionStatus } from "../../shared/types";

interface StatusPanelProps {
  channel?: Channel;
  session?: PlaybackSession;
  status?: SessionStatus;
  playbackMessage: string;
  sessionReloads: number;
  onRestart: () => void;
}

export function StatusPanel({ channel, session, status, playbackMessage, sessionReloads, onRestart }: StatusPanelProps) {
  return (
    <aside className="status-panel">
      <div className="status-heading">
        <div>
          <span>Session</span>
          <h2>{status?.healthy ? "Healthy" : session ? "Recovering" : "Idle"}</h2>
        </div>
        <button className="icon-button" type="button" onClick={onRestart} disabled={!channel} title="Restart stream" aria-label="Restart stream">
          <RotateCcw size={17} />
        </button>
      </div>

      <div className="metric-grid">
        <Metric icon={<Activity size={17} />} label="Buffer" value={`${Math.round(status?.bufferSeconds ?? 0)}s`} />
        <Metric icon={<Wifi size={17} />} label="Segments" value={String(status?.cachedSegments ?? 0)} />
        <Metric icon={<RefreshCw size={17} />} label="Retries" value={String(status?.retryCount ?? 0)} />
        <Metric icon={<RotateCcw size={17} />} label="Reloads" value={String(sessionReloads)} />
      </div>

      <div className="status-line">
        <span>Playback</span>
        <strong>{playbackMessage}</strong>
      </div>

      <div className="status-line">
        <span>Source seq</span>
        <strong>{status?.sourceSequence ?? "--"}</strong>
      </div>

      <div className="status-line">
        <span>Target</span>
        <strong>{status?.targetDuration ? `${status.targetDuration}s` : "--"}</strong>
      </div>

      <div className="status-line">
        <span>Channel</span>
        <strong>{channel?.name ?? "--"}</strong>
      </div>

      {status?.lastError ? (
        <div className="status-error">
          <AlertTriangle aria-hidden="true" size={17} />
          <span>{status.lastError}</span>
        </div>
      ) : null}
    </aside>
  );
}

function Metric({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="metric">
      {icon}
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}
